/**
 * A KULCSBEVITEL SZERKESZTŐ-ÁLLAPOTA — cégválasztás, űrlap, mentés.
 *
 * ⚠️ AZÉRT KÜLÖN HOROG, MERT KÉT FELÜLET HASZNÁLJA (#25/4). A kulcsbevitel
 * ma a Beállítások lap NAV és Tensoft szeletén áll (`NavSettings`), ÉS
 * megmarad a palettán is (`NavCredentialsDialog`) — Viktor döntése
 * artboardon, 2026-09-13: „A", a beágyazott panel.
 *
 * Ha a kettő külön tartaná a kiválasztást és az űrlapot, a két felület
 * CSENDBEN elcsúszna: a cégváltásnál ürülő mezők vagy a mentés utáni törlés
 * az egyikben megvolna, a másikban nem — és mindkettő TITKOT szivárogtat.
 * Ezért ül minden ilyen szabály itt, egy helyen.
 *
 * A `useNavCredentials` (a hálózat) VÁLTOZATLAN: ez a horog fölé ül, nem
 * helyette.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchesQuery } from '@/utils/fold';
import {
  type NavCredentialInput,
  type NavCredentialStatus,
  type NavCredentialsStatus,
  useNavCredentials,
} from './useNavCredentials';

const EMPTY_FORM: NavCredentialInput = {
  login: '',
  password: '',
  signingKey: '',
  exchangeKey: '',
};

export interface NavCredentialEditor {
  status: NavCredentialsStatus;
  detail: string;
  /** A keresőre illeszkedő cégek — név ÉS adószám alapján. */
  matches: NavCredentialStatus[];
  /** Hány cégnél van bekötés, és hány cég van összesen. */
  configuredCount: number;
  totalCount: number;
  query: string;
  setQuery: (next: string) => void;
  selectedId: string | null;
  selected: NavCredentialStatus | null;
  pick: (company: NavCredentialStatus) => void;
  form: NavCredentialInput;
  setForm: (next: NavCredentialInput) => void;
  /** Mentés a kiválasztott céghez. Cég nélkül nem csinál semmit. */
  submit: () => void;
  /** A nyugtázás után vissza a kiválasztás elé — a lista frissül. */
  next: () => void;
  /**
   * MELYIK CÉGNEK MENT A MENTÉS — nem az, ami most ki van jelölve.
   * `null`, amíg nem történt mentés ebben a menetben.
   */
  savedFor: NavCredentialStatus | null;
  saving: boolean;
  saved: boolean;
}

export function useNavCredentialEditor(): NavCredentialEditor {
  const { status, companies, detail, save, reload } = useNavCredentials();

  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<NavCredentialInput>(EMPTY_FORM);

  /**
   * ⚠️ A NYUGTÁZÁS A MENTÉS CÉLJÁT MONDJA KI, NEM AZ AKTUÁLIS KIJELÖLÉST.
   *
   * A kereső mentés közben is kattintható — szándékosan: egy néma eldobás a
   * #25/3 tanulsága szerint rosszabb, mint a késleltetés (a kattintás nem
   * történik meg, és semmi nem mondja meg, miért). Viszont ha a kijelölés
   * elmozdul, MIELŐTT az „A" cég mentése visszaér, a nyugtázás a „B" cég
   * nevét írná ki — miközben az „A"-nak írtunk.
   *
   * A könyvelőnek ez nem apró félreírás: azt olvassa, hogy B be van kötve, a
   * frissült lista viszont azt mondja, nincs — és újra beírja a kulcsokat,
   * emlékezetből, rosszul. Ezért ül a cél a mentés PILLANATÁBAN rögzítve.
   */
  const [savedFor, setSavedFor] = useState<NavCredentialStatus | null>(null);

  /**
   * A SZŰRÉS NÉVRE ÉS ADÓSZÁMRA IS MEGY, ugyanazzal a hajtogatással, amit a
   * paletta és a listák keresője használ (`@/utils/fold`). Adószámra azért,
   * mert a hosszú magyar cégnevek közt („…Kereskedelmi és Szolgáltató Kft.")
   * az adószám a rövidebb út — és a könyvelő a NAV felületéről épp azt
   * másolja ki.
   */
  const matches = useMemo(
    () =>
      companies.filter((company) =>
        matchesQuery(`${company.companyName} ${company.taxNumber}`, query)
      ),
    [companies, query]
  );

  const selected = useMemo(
    () => companies.find((company) => company.companyId === selectedId) ?? null,
    [companies, selectedId]
  );

  const configuredCount = useMemo(
    () => companies.filter((company) => company.configured).length,
    [companies]
  );

  const pick = useCallback((company: NavCredentialStatus) => {
    setSelectedId(company.companyId);
    // ⚠️ A `savedFor`-t NEM nullázzuk itt, és ez a teszt által kimért rész: a
    // kereső mentés KÖZBEN is kattintható, tehát egy ilyen kattintás épp azt
    // a célt törölné, amit meg akarunk őrizni. A nyugtázás a `next()`-tel
    // ürül, ami az egyetlen hely, ahol a menet valóban lezárul.
    // ⚠️ A MEZŐK ÜRÜLNEK CÉGVÁLTÁSNÁL. Enélkül az egyik cégnek beírt jelszó
    // ott maradna a másik cég űrlapján — és a mentés a MÁSIK céghez írná be.
    // Nyolcvan cégnél ez nem elméleti: a kereső épp arra van, hogy sorban
    // többet lehessen bekötni.
    setForm(EMPTY_FORM);
  }, []);

  const submit = useCallback(() => {
    if (selected === null) {
      return;
    }
    setSavedFor(selected);
    save(selected.companyId, form);
  }, [form, save, selected]);

  /**
   * A NYUGTÁZÁS UTÁN A LISTA FRISSÜL, DE A FELÜLET NEM ZÁRUL BE.
   *
   * Szándékos: a kereső azért van, hogy egymás után több céget lehessen
   * bekötni. Egy automatikusan záruló ablak minden cégnél újranyitást és új
   * keresést kérne — nyolcvanszor.
   */
  const next = useCallback(() => {
    setSelectedId(null);
    setSavedFor(null);
    setForm(EMPTY_FORM);
    reload();
  }, [reload]);

  const saving = status === 'saving';
  const saved = status === 'saved';

  /**
   * A SIKERES MENTÉS TÖRLI A BEÍRT TITKOKAT A REACT-ÁLLAPOTBÓL.
   *
   * ⚠️ ÉS EZT NEM A DOM-TESZT ŐRZI — mérve (2026-09-10). Az eredeti hibát
   * („a jelszó a mentés után is ott állt a DOM-ban") KÉT javítás közül
   * bármelyik megoldja: ez az effekt, vagy az űrlap eltüntetése a `saved`
   * ágon. A DOM-teszt nem választja szét őket, tehát ez a sor önmagában
   * NINCS teszttel lefogva — a mellette lévő UX-őr csak az űrlap eltűnését
   * pinneli.
   *
   * Akkor miért van itt? Mert a kettő MÁST véd. Az eltüntetés azt, hogy a
   * titok ne legyen a DOM-ban; ez pedig azt, hogy a React-állapotban se
   * MARADJON — egy nyitva hagyott felület a könyvelő gépén különben egy cég
   * teljes kredencia-készletét tartaná a memóriában, a React fejlesztői
   * eszközeiből olvashatóan, a `type="password"` mögött is (az ELFEDI a
   * beírást, nem törli).
   */
  useEffect(() => {
    if (saved) {
      setForm(EMPTY_FORM);
    }
  }, [saved]);

  return {
    status,
    detail,
    matches,
    configuredCount,
    totalCount: companies.length,
    query,
    setQuery,
    selectedId,
    selected,
    pick,
    form,
    setForm,
    submit,
    next,
    savedFor,
    saving,
    saved,
  };
}
